import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {HOME, EPOCH, ALIAS_TO_COMPETITION_ID} from '@constants';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import Dialog from '@jobseeker/lotus/components/Dialog';
import Loader from '@jobseeker/lotus/components/Loader';
import {
  RewardSection,
  HomeNavigation,
  AboutCompetitionSection,
  AboutIIMJobsSection,
  ContactSection,
  EligibilitySection,
  FAQSection,
  // PartnerCompaniesSection,
  RoundAndTimelineSection,
  CompetitionBanner,
  ParticipationBanner,
} from '@components/Competition';
import {isCSR} from '@jobseeker/lotus/utils';
import {SeoIntrup} from '@layouts/SeoLayout';

import dynamic from 'next/dynamic';
import type {AppState} from '@jobseeker/common-core/types';
import {competitionService} from '@jobseeker/common-core/services';
import type {GetServerSidePropsContext} from 'next/types';

const LoginForm = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/AuthenticationHarness/AuthForm'
    ),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

type EpochProps = {
  seoConfig: object;
  metaData: object;
};

const Epoch = ({seoConfig, metaData}: EpochProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  const enableLogin = useSelector(
    (state: AppState) => state.app.layout.enableLoginPopup
  );

  useValidateAuth(dispatch);

  const {
    timelines: {
      config: {competitionName},
    },
  } = useSelector((state: AppState) => state.competition);

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout page={EPOCH}>
        <ResponsiveLayout
          breadcrumbElements={[]}
          variant={VersionVariants.VERSION_TWO}
        >
          {isCSR() && (
            <SeoIntrup
              pageNo={0}
              nextPage={false}
              type={competitionName as 'epoch' | 'x-clause'}
            />
          )}

          <CompetitionBanner />
          <RewardSection />
          <HomeNavigation />
          <AboutCompetitionSection />
          {/* <PartnerCompaniesSection /> */}
          <RoundAndTimelineSection />
          <EligibilitySection />
          <ParticipationBanner />
          <ContactSection />
          <FAQSection />
          <AboutIIMJobsSection />
          {enableLogin && (
            <Dialog
              title="Login to your account"
              subtitle="Welcome back! 👋"
              invertedTitle
              variant={VersionVariants.VERSION_TWO}
              open={enableLogin}
              onClose={() =>
                dispatch({
                  type: 'AppContext/set_login_popup',
                  payload: false,
                })
              }
            >
              <LoginForm
                onSuccess={() => {
                  window.location.reload();
                }}
                variant={VersionVariants.VERSION_TWO}
                signUpLink={`/${competitionName}/register`}
              />
            </Dialog>
          )}
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, EPOCH, true, EPOCH);
    console.log('🚀 ~ store=> ~ metaData:', metaData);

    /**
     * Mandatory for unique routes agreement
     * Only allow the application routes to access the page
     */
    const {
      routesAllowed,
    }: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
      metaData || {};

    if (!routesAllowed?.includes(EPOCH)) {
      return {
        notFound: true,
      };
    }

    if (!metaData.featureFlags.competition) {
      return {
        notFound: true,
      };
    }
    const seoConfig = await loadSEO(HOME);

    const alias = (context.query.alias as string) || 'epoch';

    const competitionId = ALIAS_TO_COMPETITION_ID[alias];

    const timelineInfo = await competitionService.fetchTimelineData(
      competitionId
    );

    if (
      timelineInfo?.registration &&
      Object.keys(timelineInfo?.registration).length > 0
    ) {
      store.dispatch({
        type: 'Competition/set_timeline_data',
        payload: {competition: timelineInfo},
      });
    }

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: EPOCH},
    });

    return {
      props: {
        seoConfig,
        metaData,
      },
    };
  }
);

export default Epoch;
